import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import soundtrack from "./assets/soundtrack.mp3";

const GRID_W = 11;
const GRID_H = 11;
const CELL = 1.2;
const ENEMY_COUNT = 4;

function createGrid(level = 1) {
  // Density: 0.55 at lvl 1 → 0.44 at lvl 4 → 0.10 at lvl 13+
  const fastDensity = Math.max(0.10, 0.55 - (level - 1) * 0.0185);
  const grid = [];
  for (let y = 0; y < GRID_H; y++) {
    grid[y] = [];
    for (let x = 0; x < GRID_W; x++) {
      grid[y][x] = Math.random() < fastDensity ? 1 : 0;
    }
  }
  grid[5][5] = 0;
  grid[5][4] = 0;
  grid[4][5] = 0;
  grid[5][6] = 0;
  grid[6][5] = 0;
  return grid;
}

function gridToWorld(gx, gy) {
  return {
    x: (gx - GRID_W / 2 + 0.5) * CELL,
    z: (gy - GRID_H / 2 + 0.5) * CELL,
  };
}

function findRespawnCell(grid, enemies) {
  // Score every free cell by its minimum distance from any enemy
  // Pick the free cell farthest from all enemies, preferring center
  let bestCell = { gx: Math.floor(GRID_W / 2), gy: Math.floor(GRID_H / 2) };
  let bestScore = -1;

  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      if (grid[y][x]) continue; // occupied
      // Min distance to any enemy
      const minEnemyDist = enemies.length === 0 ? 999
        : Math.min(...enemies.map(en => Math.abs(en.gx - x) + Math.abs(en.gy - y)));
      // Slight preference for center
      const centerDist = Math.abs(x - Math.floor(GRID_W / 2)) + Math.abs(y - Math.floor(GRID_H / 2));
      const score = minEnemyDist * 10 - centerDist;
      if (score > bestScore) {
        bestScore = score;
        bestCell = { gx: x, gy: y };
      }
    }
  }
  return bestCell;
}

function createSfxPlayer() {
  // Create and manage simple synthesized sound effects for gameplay events.
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return {
      // Ignore sound effect requests when Web Audio is unavailable.
      playEnemyKill: () => {},
      // Ignore sound effect requests when Web Audio is unavailable.
      playPlayerDeath: () => {},
      // Ignore sound effect requests when Web Audio is unavailable.
      playBlockMove: () => {},
      // Ignore sound effect requests when Web Audio is unavailable.
      playBlockDestroy: () => {},
      // Ignore sound effect requests when Web Audio is unavailable.
      playLevelUp: () => {},
      // Ignore resume requests when Web Audio is unavailable.
      resume: () => Promise.resolve(),
      // Ignore dispose requests when Web Audio is unavailable.
      dispose: () => {},
    };
  }

  const context = new AudioContextClass();
  const master = context.createGain();
  master.gain.value = 0.24;
  master.connect(context.destination);

  const playTone = ({
    type,
    startFreq,
    endFreq,
    duration,
    volume,
    detune = 0,
    startTime = context.currentTime,
  }) => {
    // Play a single pitched tone with a quick envelope and optional frequency slide.
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, startTime);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), startTime + duration);
    osc.detune.value = detune;
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    osc.connect(gain);
    gain.connect(master);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  };

  const playEnemyKill = () => {
    // Play a sharp burst to signal an enemy explosion.
    playTone({ type: "sawtooth", startFreq: 520, endFreq: 140, duration: 0.18, volume: 0.22 });
  };

  const playPlayerDeath = () => {
    // Play a heavier two-step drop to signal the player died.
    const t0 = context.currentTime;
    playTone({ type: "square", startFreq: 320, endFreq: 140, duration: 0.22, volume: 0.26, startTime: t0 });
    playTone({ type: "triangle", startFreq: 180, endFreq: 80, duration: 0.28, volume: 0.22, startTime: t0 + 0.1 });
  };

  const playBlockMove = () => {
    // Play a short muted thump for a sliding block.
    playTone({ type: "triangle", startFreq: 210, endFreq: 160, duration: 0.08, volume: 0.14 });
  };

  const playBlockDestroy = () => {
    // Play a crisp crackle to signal a block breaking apart.
    playTone({ type: "square", startFreq: 480, endFreq: 180, duration: 0.16, volume: 0.2 });
  };

  const playLevelUp = () => {
    // Play a short two-tone chime to celebrate advancing to the next level.
    const t0 = context.currentTime;
    playTone({ type: "triangle", startFreq: 520, endFreq: 620, duration: 0.12, volume: 0.18, startTime: t0 });
    playTone({ type: "triangle", startFreq: 780, endFreq: 980, duration: 0.16, volume: 0.2, startTime: t0 + 0.12 });
  };

  const resume = () => {
    // Resume the audio context after a user gesture if it is suspended.
    return context.state === "suspended" ? context.resume() : Promise.resolve();
  };

  const dispose = () => {
    // Close the audio context to release resources when the game unmounts.
    if (context.state !== "closed") {
      context.close();
    }
  };

  return {
    playEnemyKill,
    playPlayerDeath,
    playBlockMove,
    playBlockDestroy,
    playLevelUp,
    resume,
    dispose,
  };
}

function DPadButton({ label, keyCode, keysRef }) {
  const press = useCallback((e) => {
    e.preventDefault();
    keysRef.current[keyCode] = true;
  }, [keyCode, keysRef]);

  const release = useCallback((e) => {
    e.preventDefault();
    keysRef.current[keyCode] = false;
  }, [keyCode, keysRef]);

  return (
    <div
      onPointerDown={press}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      style={{
        width: 58, height: 58,
        background: "rgba(17, 55, 130, 0.8)",
        border: "2px solid #3a6fd8",
        borderRadius: 10,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 24, color: "#c0dcff",
        userSelect: "none", cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        touchAction: "none",
        boxShadow: "0 0 14px #1133aa55, inset 0 1px 0 rgba(255,255,255,0.1)",
        transition: "background 0.08s, transform 0.08s",
        fontFamily: "monospace",
      }}
    >
      {label}
    </div>
  );
}

export default function StyxGame() {
  // Store the render target for the three.js scene.
  const mountRef = useRef(null);
  // Store mutable game state for the render loop.
  const stateRef = useRef(null);
  // Store pressed keys for keyboard/touch input.
  const keysRef = useRef({});
  // Store the touch start position for swipe detection.
  const touchStartRef = useRef(null);
  // Store the current animation frame id for cleanup.
  const animFrameRef = useRef(null);
  // Store the current level across renders.
  const currentLevelRef = useRef(1);
  // Store the soundtrack audio element instance.
  const soundtrackRef = useRef(null);
  // Store the sound effects player instance.
  const sfxRef = useRef(null);

  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [win, setWin] = useState(false);
  const [started, setStarted] = useState(false);
  const [died, setDied] = useState(false);
  const [levelComplete, setLevelComplete] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const initGame = useCallback((scene, currentLevel) => {
    // Initialize the scene meshes and state for the given level.
    if (stateRef.current) {
      const old = stateRef.current;
      old.blockMeshes.forEach((m) => scene.remove(m));
      old.enemyMeshes.forEach((m) => scene.remove(m));
      if (old.playerMesh) scene.remove(old.playerMesh);
      if (old.slidingBlock) scene.remove(old.slidingBlock.mesh);
    }

    const grid = createGrid(currentLevel);
    const blockMat = new THREE.MeshPhongMaterial({
      color: 0x88ddff, transparent: true, opacity: 0.82, shininess: 120, specular: 0xffffff,
    });
    const blockGeo = new THREE.BoxGeometry(CELL * 0.92, CELL * 0.55, CELL * 0.92);
    const blockMeshes = [];

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        if (grid[y][x]) {
          const mesh = new THREE.Mesh(blockGeo, blockMat.clone());
          const pos = gridToWorld(x, y);
          mesh.position.set(pos.x, 0.28, pos.z);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          mesh.userData = { gx: x, gy: y };
          scene.add(mesh);
          blockMeshes.push(mesh);
        }
      }
    }

    const playerMesh = new THREE.Group();
    const skinMat = new THREE.MeshPhongMaterial({ color: 0xffd4b4, emissive: 0x4a2412, shininess: 90 });
    const beardMat = new THREE.MeshPhongMaterial({ color: 0xd2a16c, emissive: 0x4a2b12, shininess: 70 });
    const hairMat = new THREE.MeshPhongMaterial({ color: 0xffe49a, emissive: 0x6a4a16, shininess: 120 });
    const briefsMat = new THREE.MeshPhongMaterial({ color: 0x2f66ff, emissive: 0x102050, shininess: 100 });
    const shirtMat = new THREE.MeshPhongMaterial({ color: 0xffffff, emissive: 0x222222, shininess: 80 });
    const eyeWhiteMat = new THREE.MeshPhongMaterial({ color: 0xffffff, emissive: 0x222222, shininess: 90 });
    const eyePupilMat = new THREE.MeshPhongMaterial({ color: 0x333333, emissive: 0x111111, shininess: 40 });

    const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.26, 0.32, 14), skinMat);
    torso.position.y = 0.2;
    torso.castShadow = true;
    playerMesh.add(torso);

    const shirt = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.27, 0.26, 14), shirtMat);
    shirt.position.y = 0.23;
    shirt.castShadow = true;
    playerMesh.add(shirt);

    const belly = new THREE.Mesh(new THREE.SphereGeometry(0.2, 16, 16), skinMat);
    belly.scale.set(1.1, 0.8, 1.1);
    belly.position.y = 0.18;
    belly.castShadow = true;
    playerMesh.add(belly);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.08, 10), skinMat);
    neck.position.y = 0.36;
    neck.castShadow = true;
    playerMesh.add(neck);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.17, 18, 18), skinMat);
    head.position.y = 0.48;
    head.castShadow = true;
    playerMesh.add(head);

    const hair = new THREE.Mesh(new THREE.SphereGeometry(0.175, 18, 18), hairMat);
    hair.scale.y = 0.6;
    hair.position.y = 0.59;
    hair.castShadow = true;
    playerMesh.add(hair);

    const hairBack = new THREE.Mesh(new THREE.SphereGeometry(0.16, 16, 16), hairMat);
    hairBack.scale.set(1.05, 0.9, 0.75);
    hairBack.position.set(0, 0.54, -0.06);
    hairBack.castShadow = true;
    playerMesh.add(hairBack);

    const beard = new THREE.Mesh(new THREE.SphereGeometry(0.135, 14, 14), beardMat);
    beard.scale.set(1.1, 0.65, 1.05);
    beard.position.set(0, 0.4, 0.1);
    beard.castShadow = true;
    playerMesh.add(beard);

    const nose = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 10), skinMat);
    nose.position.set(0, 0.47, 0.16);
    nose.castShadow = true;
    playerMesh.add(nose);

    const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 10), eyeWhiteMat);
    eyeL.position.set(-0.06, 0.5, 0.14);
    playerMesh.add(eyeL);
    const eyeR = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 10), eyeWhiteMat);
    eyeR.position.set(0.06, 0.5, 0.14);
    playerMesh.add(eyeR);
    const pupilL = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 8), eyePupilMat);
    pupilL.position.set(-0.06, 0.5, 0.17);
    playerMesh.add(pupilL);
    const pupilR = new THREE.Mesh(new THREE.SphereGeometry(0.012, 8, 8), eyePupilMat);
    pupilR.position.set(0.06, 0.5, 0.17);
    playerMesh.add(pupilR);

    const briefs = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.16, 0.26), briefsMat);
    briefs.position.y = 0.04;
    briefs.castShadow = true;
    playerMesh.add(briefs);
    const waistband = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.04, 0.27), new THREE.MeshPhongMaterial({ color: 0xffffff, emissive: 0x111111 }));
    waistband.position.y = 0.12;
    playerMesh.add(waistband);

    const legL = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.24, 12), skinMat);
    legL.position.set(-0.11, -0.14, 0);
    legL.castShadow = true;
    playerMesh.add(legL);
    const legR = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.24, 12), skinMat);
    legR.position.set(0.11, -0.14, 0);
    legR.castShadow = true;
    playerMesh.add(legR);

    const footL = new THREE.Mesh(new THREE.SphereGeometry(0.065, 12, 12), skinMat);
    footL.position.set(-0.11, -0.27, 0.06);
    playerMesh.add(footL);
    const footR = new THREE.Mesh(new THREE.SphereGeometry(0.065, 12, 12), skinMat);
    footR.position.set(0.11, -0.27, 0.06);
    playerMesh.add(footR);

    const armL = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.24, 12), skinMat);
    armL.position.set(-0.22, 0.2, 0.04);
    armL.rotation.x = 0;
    armL.rotation.z = Math.PI / 18;
    armL.castShadow = true;
    // Store the lowered arm rest rotation so push animations can return it later.
    armL.userData.restRotX = armL.rotation.x;
    // Store the lowered arm rest rotation so push animations can return it later.
    armL.userData.restRotZ = armL.rotation.z;
    playerMesh.add(armL);
    const armR = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 0.24, 12), skinMat);
    armR.position.set(0.22, 0.2, 0.04);
    armR.rotation.x = 0;
    armR.rotation.z = -Math.PI / 18;
    armR.castShadow = true;
    // Store the lowered arm rest rotation so push animations can return it later.
    armR.userData.restRotX = armR.rotation.x;
    // Store the lowered arm rest rotation so push animations can return it later.
    armR.userData.restRotZ = armR.rotation.z;
    playerMesh.add(armR);

    const handL = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 12), skinMat);
    handL.position.set(-0.22, 0.08, 0.08);
    playerMesh.add(handL);
    const handR = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 12), skinMat);
    handR.position.set(0.22, 0.08, 0.08);
    playerMesh.add(handR);

    // Store references needed for player animation on the player mesh user data.
    playerMesh.userData = {
      skinMat,
      normalSkinColor: 0xffd4b4,
      armL,
      armR,
    };
    playerMesh.castShadow = true;
    scene.add(playerMesh);

    const enemyCount = Math.min(12, 3 + Math.floor((currentLevel - 1) / 3));
    const enemyMeshes = [];
    const enemies = [];
    const ghostBodyMat = new THREE.MeshPhongMaterial({
      color: 0xff5555,
      emissive: 0x661111,
      transparent: true,
      opacity: 0.8,
      shininess: 140,
    });
    const ghostEyeMat = new THREE.MeshPhongMaterial({
      color: 0x0a0a14,
      emissive: 0x111133,
      shininess: 30,
    });
    for (let i = 0; i < enemyCount; i++) {
      let ex, ey;
      do {
        ex = Math.floor(Math.random() * GRID_W);
        ey = Math.floor(Math.random() * GRID_H);
      } while (grid[ey][ex] || (Math.abs(ex - 5) < 3 && Math.abs(ey - 5) < 3));

      const ghost = new THREE.Group();
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.25, 18, 18), ghostBodyMat);
      head.position.y = 0.22;
      head.castShadow = true;
      ghost.add(head);

      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.26, 0.32, 18), ghostBodyMat);
      body.position.y = 0.02;
      body.castShadow = true;
      ghost.add(body);

      const skirt = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.07, 8, 18), ghostBodyMat);
      skirt.rotation.x = Math.PI / 2;
      skirt.position.y = -0.15;
      skirt.castShadow = true;
      ghost.add(skirt);

      const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 10), ghostEyeMat);
      eyeL.position.set(-0.08, 0.24, 0.18);
      ghost.add(eyeL);

      const eyeR = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 10), ghostEyeMat);
      eyeR.position.set(0.08, 0.24, 0.18);
      ghost.add(eyeR);

      const pupilMat = new THREE.MeshPhongMaterial({ color: 0xffcccc, emissive: 0x552222 });
      const pupilL = new THREE.Mesh(new THREE.SphereGeometry(0.02, 8, 8), pupilMat);
      pupilL.position.set(-0.08, 0.23, 0.23);
      ghost.add(pupilL);
      const pupilR = new THREE.Mesh(new THREE.SphereGeometry(0.02, 8, 8), pupilMat);
      pupilR.position.set(0.08, 0.23, 0.23);
      ghost.add(pupilR);

      ghost.castShadow = true;
      const ePos = gridToWorld(ex, ey);
      const baseY = 0.35 + Math.random() * 0.05;
      ghost.position.set(ePos.x, baseY, ePos.z);
      scene.add(ghost);
      enemyMeshes.push(ghost);
      enemies.push({
        gx: ex,
        gy: ey,
        mesh: ghost,
        moveTimer: Math.random() * 1.2 + 0.6,
        floatPhase: Math.random() * Math.PI * 2,
        baseY,
      });
    }

    const prevLives = stateRef.current?.lives ?? 3;
    stateRef.current = {
      grid, blockMeshes, playerMesh, enemyMeshes, enemies,
      playerGx: 5, playerGy: 5, playerMoveTimer: 0,
      playerFacing: { dx: 0, dy: 1 },
      slidingBlock: null, invincible: 0,
      pushAnim: { dx: 0, dy: 0, time: 0, active: false },
      // Track the remaining time for the arm push animation.
      pushAnimTimer: 0,
      // Track the remaining time for the player squash/return scale effect on block push.
      pushScaleTimer: 0,
      lives: prevLives,
    };

    const pPos = gridToWorld(5, 5);
    playerMesh.position.set(pPos.x, 0.28, pPos.z);
  }, []);

  useEffect(() => {
    if (!started) return;

    // Start the looping soundtrack when the game begins.
    if (!soundtrackRef.current) {
      const audio = new Audio(soundtrack);
      audio.loop = true;
      audio.volume = 0.6;
      soundtrackRef.current = audio;
    }
    soundtrackRef.current.currentTime = 0;
    const soundtrackPromise = soundtrackRef.current.play();
    if (soundtrackPromise?.catch) {
      soundtrackPromise.catch(() => {});
    }

    if (!sfxRef.current) {
      sfxRef.current = createSfxPlayer();
    }
    const sfxPromise = sfxRef.current?.resume?.();
    if (sfxPromise?.catch) {
      sfxPromise.catch(() => {});
    }

    const container = mountRef.current;
    const W = container.clientWidth;
    const H = container.clientHeight;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(0x0a0a1a);
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0a0a1a, 10, 28);

    const camera = new THREE.PerspectiveCamera(55, W / H, 0.1, 100);
    const cameraOffset = new THREE.Vector3(0, 10, 10);
    camera.position.copy(cameraOffset);
    camera.lookAt(0, 0, 0);

    scene.add(new THREE.AmbientLight(0x223344, 1.2));
    const dirLight = new THREE.DirectionalLight(0xaaddff, 1.8);
    dirLight.position.set(5, 12, 8);
    dirLight.castShadow = true;
    scene.add(dirLight);
    const pLight = new THREE.PointLight(0x4488ff, 1.2, 18);
    pLight.position.set(0, 4, 0);
    scene.add(pLight);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(GRID_W * CELL + 1, GRID_H * CELL + 1),
      new THREE.MeshPhongMaterial({ color: 0x112233, shininess: 60 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    const lineMat = new THREE.LineBasicMaterial({ color: 0x224466, opacity: 0.4, transparent: true });
    for (let i = 0; i <= GRID_W; i++) {
      const x = (i - GRID_W / 2) * CELL;
      scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(x, 0.01, (-GRID_H / 2) * CELL),
        new THREE.Vector3(x, 0.01, (GRID_H / 2) * CELL)
      ]), lineMat));
    }
    for (let j = 0; j <= GRID_H; j++) {
      const z = (j - GRID_H / 2) * CELL;
      scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3((-GRID_W / 2) * CELL, 0.01, z),
        new THREE.Vector3((GRID_W / 2) * CELL, 0.01, z)
      ]), lineMat));
    }

    const wallMat = new THREE.MeshPhongMaterial({ color: 0x1a3355, emissive: 0x0a1a33 });
    [
      [0, 0.4, -(GRID_H / 2) * CELL - 0.1, new THREE.BoxGeometry(GRID_W * CELL + 0.2, 0.8, 0.2)],
      [0, 0.4, (GRID_H / 2) * CELL + 0.1, new THREE.BoxGeometry(GRID_W * CELL + 0.2, 0.8, 0.2)],
      [-(GRID_W / 2) * CELL - 0.1, 0.4, 0, new THREE.BoxGeometry(0.2, 0.8, GRID_H * CELL + 0.2)],
      [(GRID_W / 2) * CELL + 0.1, 0.4, 0, new THREE.BoxGeometry(0.2, 0.8, GRID_H * CELL + 0.2)],
    ].forEach(([x, y, z, geo]) => {
      const m = new THREE.Mesh(geo, wallMat);
      m.position.set(x, y, z);
      scene.add(m);
    });

    initGame(scene, 1);

    // Keyboard
    const onKey = (e) => { keysRef.current[e.code] = e.type === "keydown"; };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);

    // Swipe on canvas
    const canvas = renderer.domElement;
    const onTouchStart = (e) => {
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY };
    };
    const onTouchEnd = (e) => {
      if (!touchStartRef.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartRef.current.x;
      const dy = t.clientY - touchStartRef.current.y;
      touchStartRef.current = null;
      if (Math.abs(dx) < 18 && Math.abs(dy) < 18) return;
      let code;
      if (Math.abs(dx) > Math.abs(dy)) code = dx > 0 ? "ArrowRight" : "ArrowLeft";
      else code = dy > 0 ? "ArrowDown" : "ArrowUp";
      keysRef.current[code] = true;
      setTimeout(() => { keysRef.current[code] = false; }, 130);
    };
    canvas.addEventListener("touchstart", onTouchStart, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd, { passive: true });

    let lastTime = performance.now();
    let currentLevel = 1;
    const getFacingAngle = (dir) => {
      // Convert a grid direction into a facing rotation for the player mesh.
      if (!dir) return 0;
      if (dir.dy === -1) return Math.PI;
      if (dir.dy === 1) return 0;
      if (dir.dx === 1) return Math.PI / 2;
      if (dir.dx === -1) return -Math.PI / 2;
      return 0;
    };

    const startPushAnimation = (dirX, dirY) => {
      // Start a short player push animation in the given grid direction.
      const s = stateRef.current;
      if (!s) return;
      s.pushAnim = { dx: dirX, dy: dirY, time: 0, active: true };
    };

    const loop = () => {
      // Run one frame of the main game loop and render the scene.
      animFrameRef.current = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      const s = stateRef.current;
      if (!s) return;
      const keys = keysRef.current;
      s.time = (s.time ?? 0) + dt;

      const killEnemy = (en) => {
        const ep = gridToWorld(en.gx, en.gy);
        scene.remove(en.mesh);
        s.enemyMeshes.splice(s.enemyMeshes.indexOf(en.mesh), 1);
        s.enemies.splice(s.enemies.indexOf(en), 1);
        sfxRef.current?.playEnemyKill?.();
        setScore((p) => p + 300 * currentLevel);
        if (!s.particles) s.particles = [];
        // Enemy explosion particles (red/orange)
        for (let i = 0; i < 16; i++) {
          const size = 0.05 + Math.random() * 0.1;
          const pg = new THREE.OctahedronGeometry(size, 0);
          const pm = new THREE.MeshPhongMaterial({
            color: [0xff3300, 0xff6600, 0xff0000, 0xffaa00][Math.floor(Math.random() * 4)],
            emissive: 0x661100, transparent: true, opacity: 1.0,
          });
          const shard = new THREE.Mesh(pg, pm);
          shard.position.set(ep.x, 0.35, ep.z);
          const angle = (i / 16) * Math.PI * 2 + Math.random() * 0.4;
          const spd = 2 + Math.random() * 3;
          shard.userData.vx = Math.cos(angle) * spd;
          shard.userData.vy = 2 + Math.random() * 4;
          shard.userData.vz = Math.sin(angle) * spd;
          shard.userData.life = 0.5 + Math.random() * 0.4;
          shard.userData.rotSpeed = (Math.random() - 0.5) * 12;
          scene.add(shard);
          s.particles.push(shard);
        }
      };

      // Sliding block
      if (s.slidingBlock) {
        const sb = s.slidingBlock;
        const speed = 7 + currentLevel * 0.5; // cells per second
        const prevProgress = sb.progress;
        sb.progress += dt * speed;
        const currentProgress = Math.min(sb.progress, sb.distance);

        const minProgress = Math.min(prevProgress, currentProgress) - 0.001;
        const maxProgress = Math.max(prevProgress, currentProgress) + 0.001;
        sb.lastMin = minProgress;
        sb.lastMax = maxProgress;
        for (let ei = s.enemies.length - 1; ei >= 0; ei--) {
          const en = s.enemies[ei];
          const onLine = sb.dx !== 0 ? en.gy === sb.fromGy : en.gx === sb.fromGx;
          if (!onLine) continue;
          const t = (en.gx - sb.fromGx) * sb.dx + (en.gy - sb.fromGy) * sb.dy;
          if (t < 0 || t > sb.distance) continue;
          if (t >= minProgress && t <= maxProgress) {
            killEnemy(en);
          }
        }

        if (sb.progress >= sb.distance) {
          // Block lands — place it on landGx/landGy
          sb.mesh.userData.gx = sb.landGx; sb.mesh.userData.gy = sb.landGy;
          const lp = gridToWorld(sb.landGx, sb.landGy);
          sb.mesh.position.set(lp.x, 0.28, lp.z);
          s.grid[sb.landGy][sb.landGx] = 1;
          s.slidingBlock = null;
        } else {
          // Animate smoothly toward landing cell
          const startP = gridToWorld(sb.fromGx, sb.fromGy);
          sb.mesh.position.x = startP.x + sb.dx * sb.progress * CELL;
          sb.mesh.position.z = startP.z + sb.dy * sb.progress * CELL;
        }
      }

      // Player movement
      s.playerMoveTimer -= dt;
      const moveSpeed = Math.max(0.12, 0.22 - currentLevel * 0.015);
      if (s.playerMoveTimer <= 0 && !s.slidingBlock) {
        let dx = 0, dy = 0;
        if (keys["ArrowLeft"] || keys["KeyA"]) dx = -1;
        else if (keys["ArrowRight"] || keys["KeyD"]) dx = 1;
        else if (keys["ArrowUp"] || keys["KeyW"]) dy = -1;
        else if (keys["ArrowDown"] || keys["KeyS"]) dy = 1;

        if (dx !== 0 || dy !== 0) {
          s.playerFacing = { dx, dy };
          const nx = s.playerGx + dx, ny = s.playerGy + dy;
          if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
            if (!s.grid[ny][nx]) {
              s.playerGx = nx; s.playerGy = ny;
              s.playerMoveTimer = moveSpeed;
            } else {
              const blockMesh = s.blockMeshes.find((m) => m.userData.gx === nx && m.userData.gy === ny);
              if (blockMesh && !s.slidingBlock) {
                const nx2 = nx + dx, ny2 = ny + dy;
                const adjacentBlock = nx2 >= 0 && nx2 < GRID_W && ny2 >= 0 && ny2 < GRID_H && s.grid[ny2][nx2];

                if (adjacentBlock) {
                  startPushAnimation(dx, dy);
                  // Kick off the arm push animation when the block is crushed.
                  s.pushAnimTimer = 0.35;
                  // Kick off the player squash/return scale effect for a block push.
                  s.pushScaleTimer = 0.25;
                  // Clear grid immediately so player/enemies see it as free
                  s.grid[ny][nx] = 0;
                  s.blockMeshes.splice(s.blockMeshes.indexOf(blockMesh), 1);
                  sfxRef.current?.playBlockDestroy?.();
                  setScore((p) => p + 50 * currentLevel);

                  // Spawn explosion particles + animate the block itself bursting
                  const ep = gridToWorld(nx, ny);
                  if (!s.particles) s.particles = [];

                  // The block itself becomes an exploding chunk
                  blockMesh.userData.exploding = true;
                  blockMesh.userData.life = 1.0;
                  blockMesh.userData.vx = 0;
                  blockMesh.userData.vy = 3.5;
                  blockMesh.userData.vz = 0;
                  s.particles.push(blockMesh);

                  // Ice shards flying outward
                  for (let i = 0; i < 22; i++) {
                    const size = 0.05 + Math.random() * 0.12;
                    const pg = i % 3 === 0
                      ? new THREE.BoxGeometry(size, size, size)
                      : new THREE.SphereGeometry(size, 4, 4);
                    const pm = new THREE.MeshPhongMaterial({
                      color: [0x88eeff, 0xffffff, 0x44ccff, 0xaaeeff, 0x66bbff][Math.floor(Math.random() * 5)],
                      emissive: 0x1144aa,
                      transparent: true, opacity: 1.0,
                    });
                    const shard = new THREE.Mesh(pg, pm);
                    shard.position.set(ep.x, 0.3, ep.z);
                    const angle = (i / 22) * Math.PI * 2 + Math.random() * 0.4;
                    const speed = 2 + Math.random() * 3.5;
                    shard.userData.vx = Math.cos(angle) * speed;
                    shard.userData.vy = 1.5 + Math.random() * 4;
                    shard.userData.vz = Math.sin(angle) * speed;
                    shard.userData.life = 0.6 + Math.random() * 0.5;
                    shard.userData.rotSpeed = (Math.random() - 0.5) * 10;
                    scene.add(shard);
                    s.particles.push(shard);
                  }
                  s.playerMoveTimer = moveSpeed;
                } else {
                  // Walk forward from the block's current cell to find where it lands
                  let tx = nx, ty = ny;
                  // Advance until the next cell is a wall, another block, or out of bounds
                  while (true) {
                    const nx2 = tx + dx, ny2 = ty + dy;
                    // Border acts as wall — stop here
                    if (nx2 < 0 || nx2 >= GRID_W || ny2 < 0 || ny2 >= GRID_H) break;
                    // Another block — stop here
                    if (s.grid[ny2][nx2]) break;
                    // Enemies are NOT obstacles — block passes through them
                    tx = nx2; ty = ny2;
                  }
                  const landGx = tx, landGy = ty;
                  const distance = Math.abs(landGx - nx) + Math.abs(landGy - ny);

                  if (distance === 0) return;

                  s.slidingBlock = {
                    mesh: blockMesh,
                    fromGx: nx, fromGy: ny,
                    landGx, landGy,
                    dx, dy, distance,
                    progress: 0,
                  };
                  startPushAnimation(dx, dy);
                  // Kick off the arm push animation when the block starts sliding.
                  s.pushAnimTimer = 0.35;
                  // Kick off the player squash/return scale effect for a block push.
                  s.pushScaleTimer = 0.25;
                  sfxRef.current?.playBlockMove?.();
                  s.grid[ny][nx] = 0;
                  s.playerMoveTimer = moveSpeed;
                } // end else (no adjacent block)
              }
            }
          }
        }
      }

      // Apply a subtle push and recoil offset while the player is pushing a block.
      let pushOffsetX = 0;
      let pushOffsetZ = 0;
      if (s.pushAnim?.active) {
        s.pushAnim.time += dt;
        const pushIn = 0.08;
        const recoil = 0.08;
        const total = pushIn + recoil;
        const maxPush = CELL * 0.12;
        const maxRecoil = -CELL * 0.05;
        let magnitude = 0;
        if (s.pushAnim.time <= pushIn) {
          magnitude = (s.pushAnim.time / pushIn) * maxPush;
        } else if (s.pushAnim.time <= total) {
          const t = (s.pushAnim.time - pushIn) / recoil;
          magnitude = maxPush + (maxRecoil - maxPush) * t;
        } else {
          s.pushAnim.active = false;
        }
        pushOffsetX = s.pushAnim.dx * magnitude;
        pushOffsetZ = s.pushAnim.dy * magnitude;
      }

      const pPos = gridToWorld(s.playerGx, s.playerGy);
      const targetX = pPos.x + pushOffsetX;
      const targetZ = pPos.z + pushOffsetZ;
      s.playerMesh.position.x += (targetX - s.playerMesh.position.x) * 0.25;
      s.playerMesh.position.z += (targetZ - s.playerMesh.position.z) * 0.25;
      const targetAngle = getFacingAngle(s.playerFacing);
      s.playerMesh.rotation.y += (targetAngle - s.playerMesh.rotation.y) * 0.2;

      // Animate the player arms so they lift up only during a block push and return to the lowered rest pose.
      if (s.pushAnimTimer > 0 && s.playerMesh.userData?.armL && s.playerMesh.userData?.armR) {
        // Reduce the remaining animation time for the arm lift effect during a push.
        s.pushAnimTimer -= dt;
        const pushDuration = 0.35;
        const progress = 1 - Math.max(0, s.pushAnimTimer) / pushDuration;
        const phase = progress * Math.PI;
        const swing = Math.sin(phase);
        const armL = s.playerMesh.userData.armL;
        const armR = s.playerMesh.userData.armR;
        const targetRotX = -Math.PI / 2.2;
        const zTilt = Math.PI / 14;
        const targetRotZL = armL.userData.restRotZ + Math.sign(armL.userData.restRotZ || 1) * zTilt;
        const targetRotZR = armR.userData.restRotZ + Math.sign(armR.userData.restRotZ || -1) * zTilt;
        // Apply the sine-driven lift between the lowered rest rotation and the push rotation.
        armL.rotation.x = armL.userData.restRotX + swing * (targetRotX - armL.userData.restRotX);
        // Apply the sine-driven lift between the lowered rest rotation and the push rotation.
        armR.rotation.x = armR.userData.restRotX + swing * (targetRotX - armR.userData.restRotX);
        // Apply a slight outward tilt so the lifted pose reads more clearly.
        armL.rotation.z = armL.userData.restRotZ + swing * (targetRotZL - armL.userData.restRotZ);
        // Apply a slight outward tilt so the lifted pose reads more clearly.
        armR.rotation.z = armR.userData.restRotZ + swing * (targetRotZR - armR.userData.restRotZ);
      } else if (s.playerMesh.userData?.armL && s.playerMesh.userData?.armR) {
        const armL = s.playerMesh.userData.armL;
        const armR = s.playerMesh.userData.armR;
        // Reset arm rotations to their stored rest values when the push ends.
        armL.rotation.x = armL.userData.restRotX;
        // Reset arm rotations to their stored rest values when the push ends.
        armR.rotation.x = armR.userData.restRotX;
        // Reset arm tilt to the stored rest values when the push ends.
        armL.rotation.z = armL.userData.restRotZ;
        // Reset arm tilt to the stored rest values when the push ends.
        armR.rotation.z = armR.userData.restRotZ;
      }

      // Animate a quick squash and return on the player mesh only when a block push is triggered.
      if (s.pushScaleTimer > 0) {
        // Reduce the remaining time for the push squash effect.
        s.pushScaleTimer -= dt;
        const scaleDuration = 0.25;
        const squashDuration = 0.08;
        const minScale = 0.95;
        const elapsed = Math.min(scaleDuration, Math.max(0, scaleDuration - s.pushScaleTimer));
        let scaleValue = 1;
        if (elapsed <= squashDuration) {
          const t = elapsed / squashDuration;
          scaleValue = 1 + (minScale - 1) * t;
        } else {
          const t = (elapsed - squashDuration) / (scaleDuration - squashDuration);
          const easeOut = t * (2 - t);
          scaleValue = minScale + (1 - minScale) * easeOut;
        }
        s.playerMesh.scale.set(scaleValue, scaleValue, scaleValue);
      } else {
        // Return to full scale when the push squash effect is not active.
        s.playerMesh.scale.set(1, 1, 1);
      }

      // Particle update
      if (s.particles && s.particles.length > 0) {
        s.particles = s.particles.filter(p => {
          p.userData.life -= dt * 1.6;
          if (p.userData.life <= 0) { scene.remove(p); return false; }
          p.position.x += p.userData.vx * dt;
          p.position.y += p.userData.vy * dt;
          p.position.z += p.userData.vz * dt;
          p.userData.vy -= 7 * dt; // gravity
          if (p.userData.rotSpeed) p.rotation.z += p.userData.rotSpeed * dt;
          const life = Math.max(0, p.userData.life);
          if (p.material && p.material.transparent) p.material.opacity = life;
          const sc = p.userData.exploding ? life * 1.2 : life;
          p.scale.set(sc, sc, sc);
          return true;
        });
      }

      // Enemy AI
      s.enemies.forEach((en) => {
        en.moveTimer -= dt;
        if (en.moveTimer <= 0) {
          en.moveTimer = Math.max(0.3, 0.8 - currentLevel * 0.04) + Math.random() * 0.4;
          let dx, dy;
          if (Math.random() < 0.55 + currentLevel * 0.04) {
            const ddx = s.playerGx - en.gx, ddy = s.playerGy - en.gy;
            if (Math.abs(ddx) >= Math.abs(ddy)) { dx = Math.sign(ddx); dy = 0; }
            else { dx = 0; dy = Math.sign(ddy); }
          } else {
            const dirs = [{ dx: 1, dy: 0 }, { dx: -1, dy: 0 }, { dx: 0, dy: 1 }, { dx: 0, dy: -1 }];
            ({ dx, dy } = dirs[Math.floor(Math.random() * 4)]);
          }
          const nx = en.gx + dx, ny = en.gy + dy;
          const occupiedByEnemy = s.enemies.some(other => other !== en && other.gx === nx && other.gy === ny);
          if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H && !s.grid[ny][nx] && !occupiedByEnemy) { en.gx = nx; en.gy = ny; }
          if (s.slidingBlock?.lastMin !== undefined) {
            const sb = s.slidingBlock;
            const onLine = sb.dx !== 0 ? en.gy === sb.fromGy : en.gx === sb.fromGx;
            if (onLine) {
              const t = (en.gx - sb.fromGx) * sb.dx + (en.gy - sb.fromGy) * sb.dy;
              if (t >= sb.lastMin && t <= sb.lastMax) {
                killEnemy(en);
                return;
              }
            }
          }
          const ep = gridToWorld(en.gx, en.gy);
          en.mesh.position.x = ep.x; en.mesh.position.z = ep.z;
        }
        en.mesh.rotation.y += dt * 0.8;
        en.mesh.position.y = en.baseY + Math.sin(s.time * 2 + en.floatPhase) * 0.06;

        if (s.invincible <= 0 && en.gx === s.playerGx && en.gy === s.playerGy) {
          s.invincible = 99; // freeze until player confirms
          s.lives = (s.lives ?? 3) - 1;
          setLives(s.lives);
          sfxRef.current?.playPlayerDeath?.();
          if (s.lives <= 0) {
            setGameOver(true);
          } else {
            setDied(true); // show "sei morto!" modal — game loop keeps running but player is frozen
          }
          s.playerMesh.userData.skinMat.color.set(0xff4444);
        } else if (s.invincible > 0) { s.invincible -= dt; }
      });


      if (s.pendingNextLevel) {
        s.pendingNextLevel = false;
        s.levelDone = false;
        initGame(scene, currentLevelRef.current);
        return;
      }

      if (s.enemies.length === 0 && !s.levelDone) {
        s.levelDone = true;
        currentLevel++;
        currentLevelRef.current = currentLevel;
        setLevel(currentLevel);
        if (currentLevel > 99) { setWin(true); return; }
        sfxRef.current?.playLevelUp?.();
        setLevelComplete(true); // show modal — loop keeps rendering but game is paused
      }

      const camTarget = new THREE.Vector3(
        s.playerMesh.position.x + cameraOffset.x,
        cameraOffset.y,
        s.playerMesh.position.z + cameraOffset.z
      );
      camera.position.lerp(camTarget, 0.08);
      camera.lookAt(s.playerMesh.position.x, 0, s.playerMesh.position.z);
      pLight.position.x = s.playerMesh.position.x;
      pLight.position.z = s.playerMesh.position.z;
      renderer.render(scene, camera);
    };

    loop();

    const handleResize = () => {
      const w = container.clientWidth, h = container.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("resize", handleResize);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchend", onTouchEnd);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      if (soundtrackRef.current) {
        soundtrackRef.current.pause();
        soundtrackRef.current.currentTime = 0;
      }
      if (sfxRef.current) {
        sfxRef.current.dispose?.();
        sfxRef.current = null;
      }
    };
  }, [started, initGame]);

  const restart = () => {
    if (stateRef.current) stateRef.current.lives = 3;
    currentLevelRef.current = 1;
    setScore(0); setLives(3); setLevel(1);
    setGameOver(false); setWin(false); setDied(false); setLevelComplete(false); setStarted(false);
    setTimeout(() => setStarted(true), 50);
  };

  const handleNextLevel = () => {
    setLevelComplete(false);
    if (stateRef.current) stateRef.current.pendingNextLevel = true;
  };

  const handleRespawn = () => {
    const s = stateRef.current;
    if (!s) return;
    const { gx: rx, gy: ry } = findRespawnCell(s.grid, s.enemies);
    s.playerGx = rx; s.playerGy = ry;
    s.playerMoveTimer = 0;
    const rPos = gridToWorld(rx, ry);
    s.playerMesh.position.set(rPos.x, 0.28, rPos.z);
    s.playerMesh.userData.skinMat.color.set(s.playerMesh.userData.normalSkinColor);
    s.invincible = 3.0; // 3s of invincibility after respawn
    setDied(false);
  };

  const hud = (
    <div style={{
      width: "100%", padding: "6px 14px",
      background: "linear-gradient(90deg, #0a1a2e, #112244, #0a1a2e)",
      borderBottom: "2px solid #2255aa",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      zIndex: 10, boxSizing: "border-box", flexShrink: 0,
    }}>
      <div style={{ color: "#88ddff", fontSize: "16px", fontWeight: "bold", letterSpacing: "4px", textShadow: "0 0 10px #4499ff" }}>
        ❄ KILLER BLOCKS ❄
      </div>
      <div style={{ display: "flex", gap: "14px", color: "#aaddff", fontSize: "12px" }}>
        <span>SCORE: <span style={{ color: "#ffdd00" }}>{score}</span></span>
        <span>LVL: <span style={{ color: "#88ff88" }}>{level}</span></span>
        <span>LIVES: <span style={{ color: "#ff6666" }}>{"♥".repeat(Math.max(0, lives))}</span></span>
      </div>
    </div>
  );

  return (
    <div style={{
      width: "100vw", height: "100svh", background: "#060a14",
      display: "flex", flexDirection: "column",
      fontFamily: "'Courier New', monospace", overflow: "hidden",
      position: "relative", touchAction: "none",
    }}>
      {hud}

      {/* Canvas area */}
      <div ref={mountRef} style={{ flex: 1, width: "100%", position: "relative", minHeight: 0 }} />

      {/* D-PAD */}
      {showControls && started && !gameOver && !win && (
        <div style={{
          position: "absolute",
          bottom: 24,
          right: 24,
          display: "grid",
          gridTemplateAreas: `". up ." "left center right" ". down ."`,
          gridTemplateColumns: "58px 58px 58px",
          gridTemplateRows: "58px 58px 58px",
          gap: 5,
          zIndex: 15,
          pointerEvents: "auto",
        }}>
          <div style={{ gridArea: "up" }}>
            <DPadButton label="▲" keyCode="ArrowUp" keysRef={keysRef} />
          </div>
          <div style={{ gridArea: "left" }}>
            <DPadButton label="◀" keyCode="ArrowLeft" keysRef={keysRef} />
          </div>
          <div style={{ gridArea: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              width: 58, height: 58, borderRadius: 10,
              background: "rgba(10, 30, 80, 0.7)",
              border: "2px solid #2a4fa0",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#2a5fd6", boxShadow: "0 0 8px #4488ff" }} />
            </div>
          </div>
          <div style={{ gridArea: "right" }}>
            <DPadButton label="▶" keyCode="ArrowRight" keysRef={keysRef} />
          </div>
          <div style={{ gridArea: "down" }}>
            <DPadButton label="▼" keyCode="ArrowDown" keysRef={keysRef} />
          </div>
        </div>
      )}

      {/* Level Complete modal */}
      {levelComplete && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(0,10,5,0.80)", zIndex: 25,
        }}>
          <div style={{
            border: "2px solid #22aa55", background: "rgba(5,20,10,0.97)",
            padding: "36px 48px", textAlign: "center",
            boxShadow: "0 0 60px #00aa4499", borderRadius: 2,
          }}>
            <div style={{
              color: "#44ff88", fontSize: "28px", fontWeight: "bold",
              letterSpacing: 3, marginBottom: 10,
              textShadow: "0 0 20px #00ff66",
            }}>
              🏅 LEVEL COMPLETED!
            </div>
            <div style={{ color: "#88ddaa", fontSize: 13, marginBottom: 8, letterSpacing: 1 }}>
              Congratulations, you completed the level!
            </div>
            <div style={{ color: "#44aa66", fontSize: 12, marginBottom: 28, letterSpacing: 2 }}>
              Start the next one...
            </div>
            <button
              onClick={handleNextLevel}
              style={{
                background: "linear-gradient(135deg, #115522, #228844)",
                color: "#ffffff", border: "1px solid #44ff88",
                padding: "13px 44px", fontSize: "14px", letterSpacing: "4px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #00aa4488", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* "Sei morto!" modal */}
      {died && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.75)", zIndex: 25,
        }}>
          <div style={{
            border: "2px solid #aa2222", background: "rgba(20,5,5,0.97)",
            padding: "36px 48px", textAlign: "center",
            boxShadow: "0 0 60px #880000cc", borderRadius: 2,
          }}>
            <div style={{
              color: "#ff3333", fontSize: "36px", fontWeight: "bold",
              letterSpacing: 4, marginBottom: 8,
              textShadow: "0 0 20px #ff0000",
              animation: "pulse 0.8s ease-in-out infinite alternate",
            }}>
              💀 YOU DIED!
            </div>
            <div style={{ color: "#aa5555", fontSize: 13, marginBottom: 28, letterSpacing: 2 }}>
              Lives remaining: <span style={{ color: "#ffdd00" }}>{"♥".repeat(Math.max(0, lives))}</span>
            </div>
            <button
              onClick={handleRespawn}
              style={{
                background: "linear-gradient(135deg, #881111, #cc2222)",
                color: "#ffffff", border: "1px solid #ff4444",
                padding: "13px 44px", fontSize: "14px", letterSpacing: "4px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #aa000088", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              OK
            </button>
          </div>
          <style>{`@keyframes pulse { from { opacity:1; } to { opacity:0.6; } }`}</style>
        </div>
      )}

      {/* Overlay */}
      {(!started || gameOver || win) && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(0,5,15,0.9)", zIndex: 20,
        }}>
          <div style={{
            border: "2px solid #2255aa", background: "rgba(5,15,35,0.98)",
            padding: "30px 36px", textAlign: "center", maxWidth: 370,
            boxShadow: "0 0 50px #1133aa99", margin: "0 16px", borderRadius: 2,
          }}>
            {win ? (
              <>
                <div style={{ color: "#ffdd00", fontSize: "26px", marginBottom: 10, letterSpacing: 4 }}>🏆 YOU WON!</div>
                <div style={{ color: "#88ddff", fontSize: 14, marginBottom: 22 }}>Final score: {score}</div>
              </>
            ) : gameOver ? (
              <>
                <div style={{ color: "#ff4444", fontSize: "26px", marginBottom: 10, letterSpacing: 4 }}>GAME OVER</div>
                <div style={{ color: "#88ddff", fontSize: 14, marginBottom: 22 }}>Score: {score}</div>
              </>
            ) : (
              <>
                <div style={{ color: "#88ddff", fontSize: "30px", fontWeight: "bold", letterSpacing: 6, marginBottom: 4, textShadow: "0 0 20px #4488ff" }}>
                  KILLER BLOCKS
                </div>
                <div style={{ color: "#4477aa", fontSize: 11, marginBottom: 20, letterSpacing: 3 }}>❄ BLOCK ARCADE ❄</div>
                <div style={{ color: "#aabbcc", fontSize: 12, lineHeight: "1.9", marginBottom: 24, textAlign: "left" }}>
                  🟡 You are the <span style={{ color: "#ffdd00" }}>blond boy</span>.<br />
                  ◆ The <span style={{ color: "#ff5555" }}>red ghosts</span> are the enemies.<br />
                  ❄ Push the <span style={{ color: "#88ddff" }}>blocks </span> onto the enemies.<br />
                  🧪 When ghosts unleash ectoplasm, they can pass through blocks, so keep moving!<br />
                  🏆 Eliminate them all to advance to the next level!<br />
                  <br />
                  <span style={{ color: "#5577aa" }}>📱 D-pad · swipe on the playfield</span><br />
                  <span style={{ color: "#446688" }}>⌨️ WASD / arrow keys</span>
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  color: "#88ddff",
                  fontSize: 12,
                  marginBottom: 20,
                  letterSpacing: 1,
                  background: "rgba(8,18,35,0.6)",
                  border: "1px solid #224488",
                  padding: "8px 12px",
                }}>
                  <span>Show on-screen controls</span>
                  <button
                    onClick={() => setShowControls((prev) => !prev)}
                    style={{
                      background: showControls ? "linear-gradient(135deg, #1a66cc, #2288ff)" : "#223355",
                      color: "#ffffff",
                      border: "1px solid #4488ff",
                      padding: "6px 14px",
                      fontSize: 11,
                      letterSpacing: 1.5,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      boxShadow: "0 0 12px #2255aa66",
                      borderRadius: 4,
                      WebkitTapHighlightColor: "transparent",
                      minWidth: 70,
                    }}
                  >
                    {showControls ? "ON" : "OFF"}
                  </button>
                </div>
              </>
            )}
            <button
              onClick={gameOver || win ? restart : () => setStarted(true)}
              style={{
                background: "linear-gradient(135deg, #1144aa, #2266cc)",
                color: "#ffffff", border: "1px solid #4488ff",
                padding: "13px 38px", fontSize: "13px", letterSpacing: "3px",
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 0 20px #2255aa88", borderRadius: 4,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {gameOver || win ? "PLAY AGAIN" : "START GAME"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
